import React, { useEffect } from 'react'

import { useConfigurationStore } from '@stores/configurationStore'
import { useAutoplaySliderItems } from './useAutoplaySliderItems'
import AutoplaySliderItem from './AutoplaySliderItem'
import AutoplaySettingsOptionButton from './AutoplaySettingsOptionButton'
import PlayNowButton from './PlayNowButton'
import { useAutoplaySettingsButtons } from './useAutoplaySettingsButtons'
import { AutoplayButtonClickedMessage, GameUIMessageType, ViewportType } from 'game-ui-types'
import { useSpinButtonSectionStore } from '@stores/spinButtonSectionStore'
import { useNavigationStore } from '@stores/navigationStore'
import { useTranslation } from '@amusnet/translations/react'
import { useSoundStore } from '@stores/soundStore'
import { SOUNDS } from '@constants/sounds'

const AutoplaySettings: React.FC = () => {
    const { t } = useTranslation()
    const items = useAutoplaySliderItems()
    const { buttons, autoplayOptions } = useAutoplaySettingsButtons()
    const currency = useConfigurationStore((state) => state.currency)
    const viewport = useConfigurationStore((state) => state.viewport)
    const selectedAutoplayIndex = useSpinButtonSectionStore((state) => state.selectedAutoplayIndex)
    const updateSelectedAutoplayIndex = useSpinButtonSectionStore((state) => state.updateSelectedAutoplayIndex)
    const updateAutoplayCount = useSpinButtonSectionStore((state) => state.updateAutoplayCount)
    const updateAutoplayMode = useSpinButtonSectionStore((state) => state.updateAutoplayMode)
    const setIsMenuOpen = useNavigationStore((state) => state.setIsMenuOpen)
    const playEngineSound = useSoundStore((state) => state.playEngineSound)

    useEffect(() => {
        if (selectedAutoplayIndex === -1) {
            updateSelectedAutoplayIndex(0)
        }
    }, [selectedAutoplayIndex])

    const startAutoplay = (index: number) => {
        const autoplayButtonClickedMessage: AutoplayButtonClickedMessage = {
            type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED,
            payload: index,
        }

        window.postMessage(autoplayButtonClickedMessage, '*')

        updateAutoplayMode(true)
        updateAutoplayCount(autoplayOptions[index] !== -1 ? autoplayOptions[index] - 1 : autoplayOptions[index])

        setIsMenuOpen(false)
        playEngineSound(SOUNDS.SPIN)
    }

    const handleButtonClick = (index: number) => {
        updateSelectedAutoplayIndex(index)
        playEngineSound(SOUNDS.SPIN)
    }

    const renderButtons = () => (
        <>
            <div className="flex gap-3 w-full flex-wrap max-w-[728px] mx-auto justify-center">
                {buttons.map((b, i) => {
                    return (
                        <AutoplaySettingsOptionButton
                            key={b.spinsAmount}
                            spins={b.spinsAmount}
                            label={b.label}
                            isActive={selectedAutoplayIndex === i}
                            onClick={() => handleButtonClick(i)}
                            id={`autoplay-button-spins-${b.spinsAmount}`}
                        />
                    )
                })}
            </div>

            <div className="flex justify-center">
                <PlayNowButton onClick={() => startAutoplay(selectedAutoplayIndex)} id="autoplay-play-now-button" />
            </div>
        </>
    )

    return (
        <div
            className="flex flex-col w-full max-w-[1052px] px-10 pb-6 mx-auto gap-12 touch-auto [&_div]:touch-auto"
            id="autoplay-settings"
        >
            {viewport !== ViewportType.MOBILE_PORTRAIT && renderButtons()}

            <div className="w-full max-w-[728px] mx-auto">
                <h3 className="text-white text-[38px] font-black leading-none" id="stop-autoplay-title">
                    {t('gameui.stopAutoplay')}
                </h3>
            </div>

            <div className="flex flex-col gap-5 w-full max-w-[728px] mx-auto">
                {items.map((i) => {
                    return <AutoplaySliderItem key={i.title} item={i} currency={currency} id="autoplay-slider-item" />
                })}
            </div>

            {viewport === ViewportType.MOBILE_PORTRAIT && renderButtons()}
        </div>
    )
}

export default AutoplaySettings
